import {
  VOICE_AT_RISK_INACTIVITY_DAYS,
  VoiceTenantAtRiskReasonEnum,
  VoiceTenantStatusEnum,
  type VoiceTenantOverview,
} from '@reach/shared-types/voice-tenant-overview';
import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@reach/web-ui/utils';
import { appRoutes } from '../../../app-routes';
import { formatLastCall } from './voice-overview-formatters';
import styles from './VoiceOverviewTenantRow.module.css';

interface VoiceOverviewTenantRowProps {
  rank: number;
  tenant: VoiceTenantOverview;
}

const AT_RISK_TOOLTIPS: Record<VoiceTenantAtRiskReasonEnum, string> = {
  [VoiceTenantAtRiskReasonEnum.NO_RECENT_CALLS]: `No calls in the last ${VOICE_AT_RISK_INACTIVITY_DAYS}+ days`,
  [VoiceTenantAtRiskReasonEnum.NO_CALLS_EVER]: 'No calls since the agent was set up',
};

export const VoiceOverviewTenantRow: React.FC<VoiceOverviewTenantRowProps> = ({ rank, tenant }) => {
  const isCancelled = tenant.voiceStatus === VoiceTenantStatusEnum.CANCELLED;
  const statusLabel = isCancelled ? 'Cancelled' : 'Active';
  const statusExplanation =
    isCancelled && tenant.voiceCancelledAt
      ? `Assist cancelled ${formatDate(tenant.voiceCancelledAt)}`
      : undefined;

  const lastCallLabel = tenant.lastCallAt ? formatLastCall(tenant.lastCallAt) : 'No calls yet';
  const atRiskExplanation = tenant.atRiskReason ? AT_RISK_TOOLTIPS[tenant.atRiskReason] : undefined;

  const tenantPath = appRoutes.TENANT_DETAIL.replace(':id', tenant.tenantId);

  return (
    <tr className={styles.row}>
      <td className={styles.rank}>{rank}</td>
      <td className={styles.tenantCell}>
        <Link className={styles.tenantLink} to={tenantPath}>
          {tenant.tenantName}
        </Link>
      </td>
      <td>
        {statusExplanation ? (
          <details className={styles.badgeDetails}>
            <summary className={styles.statusBadge} data-status={tenant.voiceStatus}>
              {statusLabel}
            </summary>
            <p>{statusExplanation}</p>
          </details>
        ) : (
          <span className={styles.statusBadge} data-status={tenant.voiceStatus}>
            {statusLabel}
          </span>
        )}
      </td>
      <td className={styles.lastCall}>{lastCallLabel}</td>
      <td>
        {tenant.atRisk &&
          (atRiskExplanation ? (
            <details className={styles.badgeDetails}>
              <summary className={styles.atRiskBadge}>At Risk</summary>
              <p>{atRiskExplanation}</p>
            </details>
          ) : (
            <span className={styles.atRiskBadge}>At Risk</span>
          ))}
      </td>
    </tr>
  );
};
